// @flow
import type { Action } from "../actions/types";
import {
  FETCH_ORDERS,
  FETCH_ORDERS_SUCCESS,
  FETCH_ORDERS_ERROR,
  SELECT_ORDER,
  FETCH_LAST_ORDER,
  FETCH_LAST_ORDER_SUCCESS,
  FETCH_LAST_ORDER_ERROR,
  FETCH_ORDER_INVOICE,
  FETCH_ORDER_INVOICE_SUCCESS,
  FETCH_ORDER_INVOICE_ERROR,
  SET_PENDING_ORDERS,
  CANCEL_PENDING_ORDER,
  CANCEL_PENDING_ORDER_SUCCESS,
  CANCEL_VS_ORDER,
  SET_ALL_LIVE_ORDERS,
  UPDATE_ORDER_STATUS_REALTIME,
  SET_SOCKET_CONNECTION,
  ADD_REALTIME_STATUS,
  CLEAR_REALTIME_STATUSES,
  ADD_MACHINE_MESSAGE,
} from "../actions/types";

export type OrderState = {|
  fetched: boolean;
  orderHistory: null | {},
  selectedOrder: null | {},
  selectedOrderInvoice: null | {},
  lastOrder: null | {},
  isLastOrder: boolean,
  error: typeof Error | null,
  pendingOrders: Array<{}>,
  cancellingPendingOrder: boolean,
  fetchingMoreOrders: boolean,
  fetchedOrderInvoice: boolean,
  liveOrders: Array<{ [key: string]: any }> | null,
  lastCancelledOrder: { orderId: string, timestamp: number } | null,
  // Real-time WebSocket fields
  socketConnected: boolean,
  realtimeStatuses: Array<{
    orderId: string,
    status: string,
    timestamp: string,
    metadata: any,
    machineId: string
  }>,
  lastRealtimeUpdate: string | null,
  // Machine-level messaging fields
  machineMessages: Array<{
    id: string,
    imei: string,
    messageType: string,
    timestamp: string,
    priority: string,
    display: {
      header: string,
      subHeader: string,
      showOnUI: boolean,
      autoDismiss: boolean,
      dismissAfter: number
    },
    metadata: any
  }>
|};

const initialState: OrderState = {
  fetched: false,
  orderHistory: null,
  selectedOrder: null,
  selectedOrderInvoice: null,
  lastOrder: null,
  isLastOrder: false,
  error: null,
  pendingOrders: [],
  cancellingPendingOrder: false,
  fetchingMoreOrders: false,
  fetchedOrderInvoice: false,
  liveOrders: [],
  lastCancelledOrder: null,
  // Real-time WebSocket initial state
  socketConnected: false,
  realtimeStatuses: [],
  lastRealtimeUpdate: null,
  // Machine-level messaging initial state
  machineMessages: [],
};

export const orderReducer = (state: OrderState = initialState, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS:
      return {
        ...state,
        orderHistory: null,
        selectedOrderInvoice: null,
        pendingOrders: [],
        fetched: false,
      }

    case FETCH_ORDERS_SUCCESS:
      return {
        ...state,
        fetched: true,
        orderHistory: payload,
      }

    case FETCH_ORDERS_ERROR:
      return {
        ...state,
        fetched: true,
        orderHistory: [],
        selectedOrder: null,
        error: payload,
      }

    case SELECT_ORDER:
      return {
        ...state,
        selectedOrder: payload,
      }

    case FETCH_LAST_ORDER:
      return {
        ...state,
        fetchedOrderInvoice: false,
        lastOrder: null,
      }

    case FETCH_LAST_ORDER_SUCCESS:
      return {
        ...state,
        fetchedOrderInvoice: true,
        lastOrder: payload,
        selectedOrder: payload,
      }

    case FETCH_LAST_ORDER_ERROR:
      return {
        ...state,
        fetchedOrderInvoice: true,
        lastOrder: null,
        selectedOrder: null,
        error: payload,
      }
    
    case FETCH_ORDER_INVOICE:
      return {
        ...state,
        fetchedOrderInvoice: false,
        selectedOrder: null,
        error: null,
      }

    case FETCH_ORDER_INVOICE_SUCCESS:
      return {
        ...state,
        fetchedOrderInvoice: true,
        selectedOrder: payload,
        error: null,
      }

    case FETCH_ORDER_INVOICE_ERROR:
      return {
        ...state,
        fetchedOrderInvoice: true,
        selectedOrder: null,
        error: payload,
      }
    
    case SET_PENDING_ORDERS:
      let pendingOrders = state.pendingOrders;
      if (Array.isArray(payload)) {
        pendingOrders = payload;
      } else {
        pendingOrders = [ payload, ...pendingOrders];
      }
      return {
        ...state,
        pendingOrders
      }

    case CANCEL_PENDING_ORDER:
    case CANCEL_VS_ORDER:
      return {
        ...state,
        cancellingPendingOrder: true,
      }

    case CANCEL_PENDING_ORDER_SUCCESS:
      return {
        ...state,
        cancellingPendingOrder: false,
        lastCancelledOrder: payload?.orderId ? {
          orderId: payload.orderId,
          timestamp: Date.now(),
        } : state.lastCancelledOrder,
      }

    case SET_ALL_LIVE_ORDERS:
      return {
        ...state,
        liveOrders: payload,
      }

    // Real-time WebSocket reducers
    case SET_SOCKET_CONNECTION:
      return {
        ...state,
        socketConnected: payload,
      }

    case UPDATE_ORDER_STATUS_REALTIME:
      return {
        ...state,
        selectedOrder: state.selectedOrder && state.selectedOrder.order_id === payload.orderId
          ? {
              ...state.selectedOrder,
              realtime_status: payload.status,
              last_realtime_update: payload.timestamp
            }
          : state.selectedOrder,
        lastRealtimeUpdate: payload.timestamp,
      }

    case ADD_REALTIME_STATUS:
      return {
        ...state,
        realtimeStatuses: [payload, ...state.realtimeStatuses.slice(0, 49)], // Keep last 50 statuses
        lastRealtimeUpdate: payload.timestamp,
      }

    case CLEAR_REALTIME_STATUSES:
      return {
        ...state,
        realtimeStatuses: [],
      }

    case ADD_MACHINE_MESSAGE:
      return {
        ...state,
        machineMessages: [
          payload,
          ...state.machineMessages.slice(0, 9) // Keep last 10 machine messages
        ],
      }

    default:
      return state;
  }
}